import { useMemo, useCallback } from 'react';
import { BaseItem } from '../dashboard.types';

export interface DataGetter<TInput = any, TOutput = any> {
  key: string;
  getter: (input: TInput) => TOutput;
  dependencies?: (keyof TInput)[];
}

export interface MemoizedDataGetterOptions {
  maxCacheSize?: number;
  ttl?: number;
}

class DataGetterCache<TInput = any, TOutput = any> {
  private cache = new Map<string, { value: TOutput; timestamp: number; dependencies: any[] }>();
  private maxSize: number;
  private ttl: number;

  constructor(options: MemoizedDataGetterOptions = {}) {
    this.maxSize = options.maxCacheSize || 100;
    this.ttl = options.ttl || 5 * 60 * 1000;
  }

  private generateKey(input: TInput, dependencies?: (keyof TInput)[]): string {
    if (dependencies && dependencies.length > 0) {
      const depValues = dependencies.map(dep => input[dep]);
      return JSON.stringify(depValues);
    }
    return JSON.stringify(input);
  }

  private isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > this.ttl;
  }

  private evictOldest(): void {
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }
  }

  get(input: TInput, getter: DataGetter<TInput, TOutput>): TOutput {
    const key = this.generateKey(input, getter.dependencies);
    const cached = this.cache.get(key);

    if (cached && !this.isExpired(cached.timestamp)) {
      if (getter.dependencies) {
        const currentDeps = getter.dependencies.map(dep => input[dep]);
        const areDepsEqual = currentDeps.every((dep, index) => dep === cached.dependencies[index]);
        if (areDepsEqual) {
          return cached.value;
        }
      } else {
        return cached.value;
      }
    }

    this.evictOldest();
    const value = getter.getter(input);
    const dependencies = getter.dependencies ? getter.dependencies.map(dep => input[dep]) : [];

    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      dependencies,
    });

    return value;
  }

  clear(): void {
    this.cache.clear();
  }

  clearExpired(): void {
    for (const [key, cached] of this.cache.entries()) {
      if (this.isExpired(cached.timestamp)) {
        this.cache.delete(key);
      }
    }
  }
}

const globalCache = new DataGetterCache();

export const useMemoizedDataGetter = <TInput, TOutput>(
  getter: DataGetter<TInput, TOutput>,
  options?: MemoizedDataGetterOptions,
) => {
  const cache = useMemo(() => new DataGetterCache<TInput, TOutput>(options), [options]);

  return useCallback(
    (input: TInput): TOutput => {
      return cache.get(input, getter);
    },
    [cache, getter],
  );
};

export const createMemoizedGetter = <TInput, TOutput>(getter: DataGetter<TInput, TOutput>) => {
  return (input: TInput): TOutput => {
    return globalCache.get(input, getter);
  };
};

export const clearGlobalCache = (): void => {
  globalCache.clear();
};

export const clearExpiredFromGlobalCache = (): void => {
  globalCache.clearExpired();
};

export const commonDataGetters = {
  getItemsByType: createMemoizedGetter<{ items: BaseItem[]; type: string }, BaseItem[]>({
    key: 'getItemsByType',
    getter: ({ items, type }) => items.filter(item => item.type === type),
    dependencies: ['items', 'type'],
  }),

  getItemsCount: createMemoizedGetter<{ items: BaseItem[] }, number>({
    key: 'getItemsCount',
    getter: ({ items }) => items.length,
    dependencies: ['items'],
  }),

  getSelectedItemsData: createMemoizedGetter<{ items: BaseItem[]; selectedIds: Set<string | number> }, BaseItem[]>({
    key: 'getSelectedItemsData',
    getter: ({ items, selectedIds }) => items.filter(item => selectedIds.has(item.id)),
    dependencies: ['items', 'selectedIds'],
  }),

  getGroupedItems: createMemoizedGetter<{ items: BaseItem[]; groupBy: keyof BaseItem }, Record<string, BaseItem[]>>({
    key: 'getGroupedItems',
    getter: ({ items, groupBy }) => {
      return items.reduce(
        (groups, item) => {
          const key = String(item[groupBy] || 'undefined');
          if (!groups[key]) {
            groups[key] = [];
          }
          groups[key].push(item);
          return groups;
        },
        {} as Record<string, BaseItem[]>,
      );
    },
    dependencies: ['items', 'groupBy'],
  }),
};
